import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { CurrencyInput } from "../../components/CurrencyInput";
import { usePagination } from "../../hooks/usePagination";
import { Pagination } from "../../components/Pagination";

interface TenantOption {
  id: string;
  slug: string;
  trade_name: string;
  legal_name: string;
}

interface CollectionItem {
  id: string;
  title: string;
  slug: string;
  description: string | null;
  is_bundle: number;
  bundle_price_cents: number | null;
  is_global: number;
  created_at: string;
  updated_at: string;
  books_count: number;
  tenants: {
    id: string;
    slug: string;
    trade_name: string;
  }[];
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function formatBRL(cents: number | null): string {
  if (cents == null) return "—";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}

export function AdminCollectionsPage() {
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [availableTenants, setAvailableTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  const pagination = usePagination(collections, 15);

  // Formulário de Criação
  const [createForm, setCreateForm] = useState({
    title: "",
    slug: "",
    description: "",
    isBundle: false,
    bundlePriceCents: null as number | null,
    isGlobal: true,
    hasTenantRestriction: false,
    selectedTenantIds: [] as string[],
  });

  // Modal de Edição
  const [editingCollection, setEditingCollection] = useState<CollectionItem | null>(null);
  const [editForm, setEditForm] = useState({
    title: "",
    slug: "",
    description: "",
    isBundle: false,
    bundlePriceCents: null as number | null,
    isGlobal: true,
    hasTenantRestriction: false,
    selectedTenantIds: [] as string[],
  });

  function loadData() {
    setLoading(true);
    Promise.all([
      apiClient.get<{ items: CollectionItem[] }>("/admin/collections"),
      apiClient.get<{ items: TenantOption[] }>("/admin/tenants").catch(() => ({ items: [] })),
    ])
      .then(([colRes, tenRes]) => {
        setCollections(colRes.items || []);
        setAvailableTenants(tenRes.items || []);
      })
      .catch((err) => {
        setStatusMessage({
          text: err instanceof ApiError ? err.message : "Erro ao carregar coleções.",
          type: "error",
        });
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadData();
  }, []);

  // Atualização automática de slug no formulário de criação se o usuário não tiver customizado
  function handleTitleChange(newTitle: string) {
    const generatedSlug = slugify(newTitle);
    setCreateForm((prev) => ({
      ...prev,
      title: newTitle,
      slug: prev.slug === "" || prev.slug === slugify(prev.title) ? generatedSlug : prev.slug,
    }));
  }

  async function handleCreateCollection(e: React.FormEvent) {
    e.preventDefault();
    setStatusMessage(null);

    const priceCents = createForm.isBundle ? createForm.bundlePriceCents : null;

    try {
      await apiClient.post("/admin/collections", {
        title: createForm.title.trim(),
        slug: createForm.slug.trim() || undefined,
        description: createForm.description.trim() || null,
        isBundle: createForm.isBundle,
        bundlePriceCents: priceCents,
        isGlobal: createForm.isGlobal,
        tenantIds: createForm.hasTenantRestriction ? createForm.selectedTenantIds : [],
      });

      setStatusMessage({ text: "Coleção cadastrada com sucesso!", type: "success" });
      setShowCreateForm(false);
      setCreateForm({
        title: "",
        slug: "",
        description: "",
        isBundle: false,
        bundlePriceCents: null,
        isGlobal: true,
        hasTenantRestriction: false,
        selectedTenantIds: [],
      });
      loadData();
    } catch (err) {
      setStatusMessage({
        text: err instanceof ApiError ? err.message : "Falha ao criar coleção.",
        type: "error",
      });
    }
  }

  function startEdit(col: CollectionItem) {
    setEditingCollection(col);
    setEditForm({
      title: col.title,
      slug: col.slug,
      description: col.description || "",
      isBundle: Boolean(col.is_bundle),
      bundlePriceCents: col.bundle_price_cents || null,
      isGlobal: Boolean(col.is_global),
      hasTenantRestriction: col.tenants.length > 0,
      selectedTenantIds: col.tenants.map((t) => t.id),
    });
  }

  async function handleUpdateCollection(e: React.FormEvent) {
    e.preventDefault();
    if (!editingCollection) return;

    setStatusMessage(null);
    const priceCents = editForm.isBundle ? editForm.bundlePriceCents : null;

    try {
      await apiClient.patch(`/admin/collections/${editingCollection.id}`, {
        title: editForm.title.trim(),
        slug: editForm.slug.trim(),
        description: editForm.description.trim() || null,
        isBundle: editForm.isBundle,
        bundlePriceCents: priceCents,
        isGlobal: editForm.isGlobal,
        tenantIds: editForm.hasTenantRestriction ? editForm.selectedTenantIds : [],
      });

      setStatusMessage({ text: "Coleção atualizada com sucesso!", type: "success" });
      setEditingCollection(null);
      loadData();
    } catch (err) {
      setStatusMessage({
        text: err instanceof ApiError ? err.message : "Falha ao atualizar coleção.",
        type: "error",
      });
    }
  }

  async function handleToggleGlobal(col: CollectionItem) {
    const nextState = col.is_global === 1 ? 0 : 1;
    try {
      await apiClient.patch(`/admin/collections/${col.id}`, {
        isGlobal: nextState === 1,
      });
      setStatusMessage({
        text: nextState === 1 ? `Coleção "${col.title}" liberada na Visão Geral Datasci!` : `Coleção "${col.title}" removida da Visão Geral pública.`,
        type: "success",
      });
      loadData();
    } catch (err) {
      setStatusMessage({
        text: err instanceof ApiError ? err.message : "Falha ao alterar visibilidade da coleção.",
        type: "error",
      });
    }
  }

  async function handleDeleteCollection(col: CollectionItem) {
    const confirmDelete = window.confirm(
      `Deseja realmente excluir a coleção "${col.title}"?\n\nOs livros associados serão mantidos no catálogo com a coleção desvinculada.`
    );
    if (!confirmDelete) return;

    try {
      await apiClient.delete(`/admin/collections/${col.id}`);
      setStatusMessage({ text: `Coleção "${col.title}" excluída.`, type: "success" });
      loadData();
    } catch (err) {
      setStatusMessage({
        text: err instanceof ApiError ? err.message : "Falha ao excluir coleção.",
        type: "error",
      });
    }
  }

  function toggleTenantSelection(tenantId: string, isCreate: boolean) {
    if (isCreate) {
      const current = createForm.selectedTenantIds;
      const next = current.includes(tenantId) ? current.filter((id) => id !== tenantId) : [...current, tenantId];
      setCreateForm({ ...createForm, selectedTenantIds: next });
    } else {
      const current = editForm.selectedTenantIds;
      const next = current.includes(tenantId) ? current.filter((id) => id !== tenantId) : [...current, tenantId];
      setEditForm({ ...editForm, selectedTenantIds: next });
    }
  }

  // Contadores
  const totalCollections = collections.length;
  const globalCollections = collections.filter((c) => c.is_global === 1).length;
  const tenantRestrictedCollections = collections.filter((c) => c.tenants && c.tenants.length > 0).length;
  const bundleCollections = collections.filter((c) => c.is_bundle === 1).length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "var(--space-4)" }}>
        <div>
          <h2 style={{ marginBottom: "var(--space-1)" }}>Coleções Editoriais & Visibilidade</h2>
          <p className="text-muted" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
            Organize obras em coleções temáticas ou pacotes (bundles), definindo visibilidade na <strong>Visão Geral da Datasci</strong> e/ou acesso restrito a <strong>Tenants Corporativos</strong>.
          </p>
        </div>
        <button
          className="btn"
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{ whiteSpace: "nowrap" }}
        >
          {showCreateForm ? "Fechar Formulário" : "+ Nova Coleção"}
        </button>
      </div>

      {statusMessage && (
        <div
          className="card"
          style={{
            padding: "var(--space-3) var(--space-4)",
            marginBottom: "var(--space-4)",
            backgroundColor: statusMessage.type === "success" ? "rgba(46, 125, 50, 0.1)" : "rgba(211, 47, 47, 0.1)",
            borderLeft: `4px solid ${statusMessage.type === "success" ? "#2e7d32" : "#d32f2f"}`,
            color: statusMessage.type === "success" ? "#1b5e20" : "#c62828",
            fontSize: "var(--text-sm)",
          }}
        >
          {statusMessage.text}
        </div>
      )}

      {/* Cards de Métricas */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "var(--space-3)",
          marginBottom: "var(--space-5)",
        }}
      >
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Total de Coleções</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700 }}>{totalCollections}</div>
        </div>
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Visão Geral Datasci (Públicas)</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "var(--color-primary, #0066cc)" }}>
            {globalCollections}
          </div>
        </div>
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Vinculadas a Tenants</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "#6b21a8" }}>
            {tenantRestrictedCollections}
          </div>
        </div>
        <div className="card" style={{ padding: "var(--space-3)" }}>
          <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Pacotes Comerciais (Bundles)</div>
          <div style={{ fontSize: "var(--text-2xl)", fontWeight: 700, color: "#d97706" }}>
            {bundleCollections}
          </div>
        </div>
      </div>

      {/* Formulário de Cadastro de Nova Coleção */}
      {showCreateForm && (
        <form onSubmit={handleCreateCollection} className="card" style={{ marginBottom: "var(--space-6)", padding: "var(--space-5)" }}>
          <h3 style={{ marginTop: 0, marginBottom: "var(--space-3)" }}>Cadastrar Nova Coleção</h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "var(--space-4)" }}>
            <div className="field">
              <label htmlFor="create-col-title">Título da Coleção *</label>
              <input
                id="create-col-title"
                required
                placeholder="Ex.: Inteligência Artificial Aplicada"
                value={createForm.title}
                onChange={(e) => handleTitleChange(e.target.value)}
              />
            </div>

            <div className="field">
              <label htmlFor="create-col-slug">Slug (URL amigável) *</label>
              <input
                id="create-col-slug"
                required
                placeholder="inteligencia-artificial-aplicada"
                value={createForm.slug}
                onChange={(e) => setCreateForm({ ...createForm, slug: slugify(e.target.value) })}
              />
              <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                Identificador na URL do catálogo: <code>/colecoes/{createForm.slug || "slug"}</code>
              </span>
            </div>
          </div>

          <div className="field" style={{ marginTop: "var(--space-3)" }}>
            <label htmlFor="create-col-desc">Descrição Editorial</label>
            <textarea
              id="create-col-desc"
              rows={2}
              placeholder="Apresentação do escopo temático, objetivos pedagógicos ou proposta da série de livros..."
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            />
          </div>

          {/* Venda em Pacote / Bundle */}
          <div
            className="card"
            style={{
              padding: "var(--space-3) var(--space-4)",
              marginTop: "var(--space-3)",
              marginBottom: "var(--space-4)",
              backgroundColor: createForm.isBundle ? "rgba(217, 119, 6, 0.05)" : "transparent",
              border: createForm.isBundle ? "1px solid #d97706" : "1px solid var(--color-border, #e5e7eb)",
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer", fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={createForm.isBundle}
                onChange={(e) => setCreateForm({ ...createForm, isBundle: e.target.checked })}
              />
              Comercializar esta Coleção como Pacote Fechado (Bundle de Obras)
            </label>
            {createForm.isBundle && (
              <div className="field" style={{ marginTop: "var(--space-3)", maxWidth: 280 }}>
                <label htmlFor="create-col-bundle-price">Preço do Pacote Completo *</label>
                <CurrencyInput
                  id="create-col-bundle-price"
                  required
                  valueCents={createForm.bundlePriceCents}
                  onChangeCents={(cents) => setCreateForm({ ...createForm, bundlePriceCents: cents })}
                />
              </div>
            )}
          </div>

          {/* SEÇÃO DE VISIBILIDADE: VISÃO GERAL DATASCI E/OU RESTRIÇÃO A TENANT */}
          <div
            className="card"
            style={{
              padding: "var(--space-4)",
              marginTop: "var(--space-3)",
              marginBottom: "var(--space-4)",
              backgroundColor: "var(--color-bg-secondary, #f8fafc)",
              border: "1px solid var(--color-border, #e2e8f0)",
            }}
          >
            <div style={{ marginBottom: "var(--space-3)" }}>
              <strong style={{ fontSize: "var(--text-base)" }}>Regras de Visibilidade & Acesso da Coleção</strong>
              <p className="text-muted" style={{ fontSize: "var(--text-xs)", margin: "var(--space-1) 0 0 0" }}>
                Você pode vincular esta coleção à visão geral da Datasci, restringi-la exclusivamente a empresas corporativas ou adotar modelo híbrido (ambos).
              </p>
            </div>

            {/* Checkbox 1: Visão Geral na Datasci */}
            <div style={{ marginBottom: "var(--space-3)", padding: "var(--space-2) var(--space-3)", background: "#fff", borderRadius: 4, border: "1px solid #e2e8f0" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-2)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  style={{ marginTop: 3 }}
                  checked={createForm.isGlobal}
                  onChange={(e) => setCreateForm({ ...createForm, isGlobal: e.target.checked })}
                />
                <div>
                  <strong>🌐 Visão Geral na Datasci (Catálogo Aberto / Loja Pública)</strong>
                  <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                    Quando ativado, a coleção fica visível para qualquer visitante no site da Datasci (página <code>/colecoes</code> e busca pública).
                  </div>
                </div>
              </label>
            </div>

            {/* Checkbox 2: Restrição a Tenant(s) */}
            <div style={{ padding: "var(--space-2) var(--space-3)", background: "#fff", borderRadius: 4, border: "1px solid #e2e8f0" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "var(--space-2)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  style={{ marginTop: 3 }}
                  checked={createForm.hasTenantRestriction}
                  onChange={(e) => setCreateForm({ ...createForm, hasTenantRestriction: e.target.checked })}
                />
                <div>
                  <strong>🏢 Restrito / Vinculado a Tenants Corporativos (Empresas)</strong>
                  <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                    Atribui esta coleção a empresas parceiras específicas, disponibilizando-a no portal corporativo do tenant (ex.: <code>/t/:tenantSlug</code>).
                  </div>
                </div>
              </label>

              {createForm.hasTenantRestriction && (
                <div style={{ marginTop: "var(--space-3)", paddingTop: "var(--space-3)", borderTop: "1px dashed #cbd5e1" }}>
                  <div style={{ fontSize: "var(--text-xs)", fontWeight: 600, marginBottom: "var(--space-2)" }}>
                    Selecione quais empresas parceiras têm acesso a esta coleção:
                  </div>
                  {availableTenants.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: "var(--text-xs)" }}>Nenhum tenant cadastrado no sistema.</p>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "var(--space-2)" }}>
                      {availableTenants.map((t) => {
                        const isSelected = createForm.selectedTenantIds.includes(t.id);
                        return (
                          <label
                            key={t.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "var(--space-2)",
                              padding: "var(--space-2) var(--space-3)",
                              background: isSelected ? "rgba(107, 33, 168, 0.08)" : "#f8fafc",
                              border: isSelected ? "1px solid #6b21a8" : "1px solid #e2e8f0",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontSize: "var(--text-xs)",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTenantSelection(t.id, true)}
                            />
                            <div>
                              <strong>{t.trade_name}</strong>
                              <div className="text-muted" style={{ fontSize: "11px" }}>slug: {t.slug}</div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Resumo dinâmico da Visibilidade */}
            <div
              style={{
                marginTop: "var(--space-3)",
                padding: "var(--space-2) var(--space-3)",
                borderRadius: 4,
                fontSize: "var(--text-xs)",
                backgroundColor:
                  createForm.isGlobal && createForm.hasTenantRestriction && createForm.selectedTenantIds.length > 0
                    ? "rgba(59, 130, 246, 0.1)"
                    : createForm.isGlobal
                    ? "rgba(34, 197, 94, 0.1)"
                    : createForm.hasTenantRestriction && createForm.selectedTenantIds.length > 0
                    ? "rgba(168, 85, 247, 0.1)"
                    : "rgba(239, 68, 68, 0.1)",
                color:
                  createForm.isGlobal && createForm.hasTenantRestriction && createForm.selectedTenantIds.length > 0
                    ? "#1d4ed8"
                    : createForm.isGlobal
                    ? "#15803d"
                    : createForm.hasTenantRestriction && createForm.selectedTenantIds.length > 0
                    ? "#7e22ce"
                    : "#b91c1c",
              }}
            >
              <strong>Resultado da Visibilidade: </strong>
              {createForm.isGlobal && createForm.hasTenantRestriction && createForm.selectedTenantIds.length > 0 ? (
                <span>🌐🏢 <strong>Híbrido:</strong> Visível publicamente na Datasci E vinculada a {createForm.selectedTenantIds.length} tenant(s).</span>
              ) : createForm.isGlobal ? (
                <span>🌐 <strong>Visão Geral Datasci:</strong> Visível na loja pública aberta da Datasci.</span>
              ) : createForm.hasTenantRestriction && createForm.selectedTenantIds.length > 0 ? (
                <span>🏢 <strong>Exclusiva / Restrita:</strong> Visível SOMENTE para os {createForm.selectedTenantIds.length} tenant(s) selecionados (oculta do público geral).</span>
              ) : (
                <span>🔒 <strong>Rascunho / Oculta:</strong> Não será exibida na loja aberta nem para tenants até ser liberada.</span>
              )}
            </div>
          </div>

          <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
            <button type="button" className="btn-secondary" onClick={() => setShowCreateForm(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn">
              Salvar Coleção
            </button>
          </div>
        </form>
      )}

      {/* Tabela de Coleções */}
      {loading ? (
        <p className="text-muted">Carregando coleções...</p>
      ) : collections.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <p className="text-muted" style={{ marginBottom: "var(--space-3)" }}>
            Nenhuma coleção editorial cadastrada até o momento.
          </p>
          <button className="btn" onClick={() => setShowCreateForm(true)}>
            Cadastrar Primeira Coleção
          </button>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="table" style={{ width: "100%", fontSize: "var(--text-sm)" }}>
            <thead>
              <tr>
                <th>Coleção</th>
                <th>Slug</th>
                <th>Obras</th>
                <th>Tipo / Preço</th>
                <th>Visibilidade</th>
                <th style={{ textAlign: "right" }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pagination.paginatedItems.map((col) => {
                const isGlobal = col.is_global === 1;
                const hasTenants = col.tenants && col.tenants.length > 0;

                return (
                  <tr key={col.id}>
                    <td>
                      <strong style={{ fontSize: "var(--text-base)" }}>{col.title}</strong>
                      {col.description && (
                        <p className="text-muted" style={{ fontSize: "var(--text-xs)", margin: "2px 0 0 0", maxWidth: 360 }}>
                          {col.description}
                        </p>
                      )}
                    </td>
                    <td>
                      <code>{col.slug}</code>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 12,
                          background: "#f1f5f9",
                          fontWeight: 600,
                        }}
                      >
                        {col.books_count} {col.books_count === 1 ? "livro" : "livros"}
                      </span>
                    </td>
                    <td>
                      {col.is_bundle === 1 ? (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: "rgba(217, 119, 6, 0.1)",
                            color: "#b45309",
                            fontWeight: 600,
                            fontSize: "var(--text-xs)",
                          }}
                        >
                          Pacote: {formatBRL(col.bundle_price_cents)}
                        </span>
                      ) : (
                        <span className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                          Coleção Temática
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {isGlobal && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              padding: "2px 8px",
                              borderRadius: 4,
                              background: "rgba(34, 197, 94, 0.15)",
                              color: "#15803d",
                              fontSize: "var(--text-xs)",
                              fontWeight: 600,
                              width: "fit-content",
                            }}
                          >
                            🌐 Visão Geral Datasci
                          </span>
                        )}
                        {hasTenants ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {col.tenants.map((t) => (
                              <span
                                key={t.id}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "2px 6px",
                                  borderRadius: 4,
                                  background: "rgba(107, 33, 168, 0.12)",
                                  color: "#6b21a8",
                                  fontSize: "11px",
                                  fontWeight: 500,
                                }}
                                title={`Restrito ao tenant: ${t.trade_name} (${t.slug})`}
                              >
                                🏢 {t.trade_name}
                              </span>
                            ))}
                          </div>
                        ) : !isGlobal ? (
                          <span
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 4,
                              background: "rgba(239, 68, 68, 0.1)",
                              color: "#b91c1c",
                              fontSize: "var(--text-xs)",
                              fontWeight: 600,
                              width: "fit-content",
                            }}
                          >
                            🔒 Oculta / Não vinculada
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        className="btn-secondary"
                        style={{
                          fontSize: "var(--text-xs)",
                          padding: "4px 8px",
                          marginRight: 6,
                          backgroundColor: isGlobal ? "rgba(34, 197, 94, 0.1)" : "rgba(239, 68, 68, 0.1)",
                          borderColor: isGlobal ? "#22c55e" : "#ef4444",
                          color: isGlobal ? "#15803d" : "#b91c1c",
                        }}
                        onClick={() => handleToggleGlobal(col)}
                        title={isGlobal ? "Remover da visão geral pública" : "Liberar na visão geral pública"}
                      >
                        {isGlobal ? "🌐 Na Datasci" : "○ Oculta na Datasci"}
                      </button>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: "var(--text-xs)", padding: "4px 8px", marginRight: 6 }}
                        onClick={() => startEdit(col)}
                      >
                        Editar
                      </button>
                      <button
                        className="btn-secondary"
                        style={{ fontSize: "var(--text-xs)", padding: "4px 8px", color: "#dc2626" }}
                        onClick={() => handleDeleteCollection(col)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination
            currentPage={pagination.currentPage}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={pagination.setCurrentPage}
            itemLabel="coleções"
          />
        </div>
      )}

      {/* Modal de Edição Completa de Coleção */}
      {editingCollection && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "var(--space-4)",
          }}
          onClick={() => setEditingCollection(null)}
        >
          <div
            className="card"
            style={{
              maxWidth: 680,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "var(--space-5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-4)" }}>
              <h3 style={{ margin: 0 }}>Editar Coleção: {editingCollection.title}</h3>
              <button
                type="button"
                className="btn-secondary"
                style={{ fontSize: "var(--text-xs)", padding: "2px 8px" }}
                onClick={() => setEditingCollection(null)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateCollection}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "var(--space-3)" }}>
                <div className="field">
                  <label htmlFor="edit-col-title">Título *</label>
                  <input
                    id="edit-col-title"
                    required
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label htmlFor="edit-col-slug">Slug *</label>
                  <input
                    id="edit-col-slug"
                    required
                    value={editForm.slug}
                    onChange={(e) => setEditForm({ ...editForm, slug: slugify(e.target.value) })}
                  />
                </div>
              </div>

              <div className="field" style={{ marginTop: "var(--space-3)" }}>
                <label htmlFor="edit-col-desc">Descrição Editorial</label>
                <textarea
                  id="edit-col-desc"
                  rows={2}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
              </div>

              {/* Pacote Comercial / Bundle */}
              <div
                className="card"
                style={{
                  padding: "var(--space-3) var(--space-4)",
                  marginTop: "var(--space-3)",
                  marginBottom: "var(--space-3)",
                  backgroundColor: editForm.isBundle ? "rgba(217, 119, 6, 0.05)" : "transparent",
                  border: editForm.isBundle ? "1px solid #d97706" : "1px solid var(--color-border, #e5e7eb)",
                }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={editForm.isBundle}
                    onChange={(e) => setEditForm({ ...editForm, isBundle: e.target.checked })}
                  />
                  Vender como Pacote Fechado (Bundle de Obras)
                </label>
                {editForm.isBundle && (
                  <div className="field" style={{ marginTop: "var(--space-3)", maxWidth: 280 }}>
                    <label htmlFor="edit-col-bundle-price">Preço do Pacote Completo *</label>
                    <CurrencyInput
                      id="edit-col-bundle-price"
                      required
                      valueCents={editForm.bundlePriceCents}
                      onChangeCents={(cents) => setEditForm({ ...editForm, bundlePriceCents: cents })}
                    />
                  </div>
                )}
              </div>

              {/* Regras de Visibilidade no Modal */}
              <div
                className="card"
                style={{
                  padding: "var(--space-3) var(--space-4)",
                  marginTop: "var(--space-3)",
                  marginBottom: "var(--space-4)",
                  backgroundColor: "var(--color-bg-secondary, #f8fafc)",
                  border: "1px solid var(--color-border, #e2e8f0)",
                }}
              >
                <strong style={{ fontSize: "var(--text-sm)" }}>Visibilidade & Acesso</strong>

                {/* Checkbox Visão Geral */}
                <div style={{ marginTop: "var(--space-2)", padding: "var(--space-2)", background: "#fff", borderRadius: 4, border: "1px solid #e2e8f0" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={editForm.isGlobal}
                      onChange={(e) => setEditForm({ ...editForm, isGlobal: e.target.checked })}
                    />
                    <span>🌐 <strong>Visão Geral na Datasci</strong> (Catálogo aberto e loja pública)</span>
                  </label>
                </div>

                {/* Checkbox Tenant */}
                <div style={{ marginTop: "var(--space-2)", padding: "var(--space-2)", background: "#fff", borderRadius: 4, border: "1px solid #e2e8f0" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={editForm.hasTenantRestriction}
                      onChange={(e) => setEditForm({ ...editForm, hasTenantRestriction: e.target.checked })}
                    />
                    <span>🏢 <strong>Restrito / Vinculado a Tenants Corporativos</strong></span>
                  </label>

                  {editForm.hasTenantRestriction && (
                    <div style={{ marginTop: "var(--space-3)", paddingTop: "var(--space-2)", borderTop: "1px dashed #cbd5e1" }}>
                      <div style={{ fontSize: "var(--text-xs)", fontWeight: 600, marginBottom: "var(--space-2)" }}>
                        Selecione os Tenants autorizados:
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "var(--space-2)" }}>
                        {availableTenants.map((t) => {
                          const isSelected = editForm.selectedTenantIds.includes(t.id);
                          return (
                            <label
                              key={t.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "var(--space-2)",
                                padding: "var(--space-2)",
                                background: isSelected ? "rgba(107, 33, 168, 0.08)" : "#f8fafc",
                                border: isSelected ? "1px solid #6b21a8" : "1px solid #e2e8f0",
                                borderRadius: 4,
                                cursor: "pointer",
                                fontSize: "var(--text-xs)",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleTenantSelection(t.id, false)}
                              />
                              <div>
                                <strong>{t.trade_name}</strong>
                                <div className="text-muted" style={{ fontSize: "11px" }}>{t.slug}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
                <button type="button" className="btn-secondary" onClick={() => setEditingCollection(null)}>
                  Cancelar
                </button>
                <button type="submit" className="btn">
                  Salvar Alterações
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
